import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cloneDeep, forEach, fromPairs, get, includes, isArray, isEmpty, isNil, map, pickBy, set } from 'lodash'
import notify from 'components/atoms/Notify'
import Modal from 'components/molecules/Modal'
import Switch from 'components/atoms/Switch'
import apiPost from 'lib/network/apiPost'
import apiPut from 'lib/network/apiPut'
import { CANCELLED_BY, Checkbox, Field, NumberInput, REFUND_TYPES, Select, TriState, btn, inputClass, isBlank, reasonOptions, toNumber } from '../refundShared'

export const NUMBER_CONDITIONS = ['hoursBeforePickupGte', 'hoursBeforePickupLt', 'hoursSincePaymentLte', 'pickupWithinHoursOfPaymentLte', 'deliveryDelayHoursGt']
export const BOOLEAN_CONDITIONS = ['sameDayAsPayment', 'withinWorkingHours', 'pickupSameDayAsPayment', 'carHandedOver', 'issueReported', 'wentToOffice', 'noShow', 'officeApproved']
export const LIST_CONDITIONS = ['cancelledBy', 'reasons', 'bookingTypes']
const DEDUCTIONS = ['deliveryFee', 'firstDayPrice', 'fxFee', 'bankFee']
const SWITCHES = ['isActive', 'isAddOn', 'requiresApproval']
const REQUIRED = ['code', 'priority', 'refundPercent', 'name.en', 'name.ar']
const CODE_PATTERN = /^[A-Z0-9_]{1,20}$/

const SectionTitle = ({ children }) => <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 border-b border-gray-200 pb-2">{children}</h4>

/** Add / edit a refund rule. Every save creates a new version on the backend; there is no delete. */
const RefundRuleModal = ({ rule, open, settings, onClose, onSaved }) => {
  const { t, i18n } = useTranslation()
  const [values, setValues] = useState(() => cloneDeep(rule || { isActive: true, isAddOn: false, requiresApproval: false, refundBase: 'paid', deductions: {}, conditions: {} }))
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const isEdit = Boolean(rule)

  const setValue = (path, value) => {
    setValues(prev => set(cloneDeep(prev), path, value))
    setErrors(prev => ({ ...prev, [path]: undefined }))
  }
  const text = path => ({ value: get(values, path) ?? '', onChange: e => setValue(path, e.target.value) })

  const listOptions = {
    cancelledBy: map(CANCELLED_BY, v => ({ value: v, label: t(`refundEngine.cancelledBy.${v}`) })),
    reasons: reasonOptions(settings, i18n.language),
    bookingTypes: map(REFUND_TYPES, v => ({ value: v, label: t(`refundEngine.refundType.${v}`) })),
  }

  const validate = () => {
    const next = {}
    forEach(REQUIRED, path => {
      if (isBlank(get(values, path))) next[path] = t('refundEngine.common.required')
    })
    if (!next.code && !CODE_PATTERN.test(values.code)) next.code = t('refundEngine.rules.fields.codeHint')
    setErrors(next)
    return isEmpty(next)
  }

  const handleSubmit = async () => {
    if (!validate()) return
    // "Any" is sent as null so an edit can clear a condition; a new rule simply omits it.
    const conditions = {}
    forEach([...NUMBER_CONDITIONS, ...BOOLEAN_CONDITIONS, ...LIST_CONDITIONS], key => {
      let value = get(values, `conditions.${key}`)
      if (includes(NUMBER_CONDITIONS, key)) value = toNumber(value)
      conditions[key] = isNil(value) || (isArray(value) && isEmpty(value)) ? null : value
    })
    const body = {
      priority: toNumber(values.priority),
      refundPercent: toNumber(values.refundPercent),
      name: { en: get(values, 'name.en'), ar: get(values, 'name.ar') },
      refundBase: values.refundBase,
      customerText: { en: get(values, 'customerText.en'), ar: get(values, 'customerText.ar') },
      isActive: values.isActive,
      isAddOn: values.isAddOn,
      requiresApproval: values.requiresApproval,
      deductions: fromPairs(map(DEDUCTIONS, key => [key, get(values, `deductions.${key}`)])),
      conditions: isEdit ? conditions : pickBy(conditions, v => v !== null),
    }
    setSaving(true)
    const response = isEdit ? await apiPut(`refund-rules/${rule.code}`, body) : await apiPost('refund-rules', { ...body, code: values.code })
    setSaving(false)
    if (response?.success) {
      notify.success(t('refundEngine.rules.saved'))
      onSaved()
    }
  }

  return (
    <Modal
      open={open}
      closeable
      onClose={onClose}
      title={isEdit ? `${t('refundEngine.rules.editRule')} — ${rule.code} (v${rule.version})` : t('refundEngine.rules.addRule')}
      dimensions={{ width: '94vw', maxWidth: '880px', height: 'auto', maxHeight: '85vh' }}
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label={t('refundEngine.rules.fields.code')} htmlFor="rule-code" error={errors.code} hint={isEdit ? null : t('refundEngine.rules.fields.codeHint')}>
            <input id="rule-code" maxLength={20} disabled={isEdit} className={inputClass(errors.code)} {...text('code')} />
          </Field>
          <Field label={t('refundEngine.rules.columns.priority')} htmlFor="rule-priority" error={errors.priority} hint={t('refundEngine.rules.fields.priorityHint')}>
            <NumberInput id="rule-priority" value={values.priority} onChange={v => setValue('priority', v)} error={errors.priority} />
          </Field>
          <Field label={t('refundEngine.rules.columns.refundPercent')} htmlFor="rule-percent" error={errors.refundPercent}>
            <NumberInput id="rule-percent" min={0} max={100} addon="%" value={values.refundPercent} onChange={v => setValue('refundPercent', v)} error={errors.refundPercent} />
          </Field>
          <Field label={t('refundEngine.rules.fields.nameEn')} htmlFor="rule-name-en" error={errors['name.en']}>
            <input id="rule-name-en" className={inputClass(errors['name.en'])} {...text('name.en')} />
          </Field>
          <Field label={t('refundEngine.rules.fields.nameAr')} htmlFor="rule-name-ar" error={errors['name.ar']}>
            <input id="rule-name-ar" dir="rtl" className={inputClass(errors['name.ar'])} {...text('name.ar')} />
          </Field>
          <Field label={t('refundEngine.rules.columns.refundBase')}>
            <Select
              value={values.refundBase}
              onChange={v => setValue('refundBase', v)}
              placeholder={t('refundEngine.rules.columns.refundBase')}
              options={map(['paid', 'deliveryFee'], v => ({ value: v, label: t(`refundEngine.refundBase.${v}`) }))}
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label={t('refundEngine.rules.fields.customerTextEn')} htmlFor="rule-text-en">
            <textarea id="rule-text-en" rows={2} className={inputClass()} {...text('customerText.en')} />
          </Field>
          <Field label={t('refundEngine.rules.fields.customerTextAr')} htmlFor="rule-text-ar">
            <textarea id="rule-text-ar" rows={2} dir="rtl" className={inputClass()} {...text('customerText.ar')} />
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {map(SWITCHES, key => (
            <div key={key} className="flex items-center justify-between gap-3 rounded-md border border-gray-200 px-3 py-2">
              <span className="text-sm font-medium text-gray-700">{t(`refundEngine.rules.fields.${key}`)}</span>
              <Switch value={Boolean(values[key])} onChange={checked => setValue(key, checked)} />
            </div>
          ))}
        </div>

        <div>
          <p className="block text-sm font-medium text-gray-700 mb-2">{t('refundEngine.rules.columns.deductions')}</p>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {map(DEDUCTIONS, key => (
              <Checkbox key={key} checked={get(values, `deductions.${key}`)} onChange={checked => setValue(`deductions.${key}`, checked)}>
                {t(`refundEngine.deductions.${key}`)}
              </Checkbox>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <SectionTitle>{t('refundEngine.rules.fields.conditions')}</SectionTitle>
          <p className="text-xs text-gray-500">{t('refundEngine.rules.fields.conditionsHint')}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {map(NUMBER_CONDITIONS, key => (
              <Field key={key} label={t(`refundEngine.conditions.${key}`)} htmlFor={`cond-${key}`}>
                <NumberInput
                  id={`cond-${key}`}
                  min={0}
                  placeholder={t('refundEngine.common.any')}
                  value={get(values, `conditions.${key}`)}
                  onChange={v => setValue(`conditions.${key}`, v)}
                />
              </Field>
            ))}
            {map(BOOLEAN_CONDITIONS, key => (
              <Field key={key} label={t(`refundEngine.conditions.${key}`)}>
                <TriState value={get(values, `conditions.${key}`)} onChange={v => setValue(`conditions.${key}`, v)} />
              </Field>
            ))}
            {map(LIST_CONDITIONS, key => (
              <Field key={key} label={t(`refundEngine.conditions.${key}`)}>
                <Select
                  multiple
                  showSearch
                  placeholder={t('refundEngine.common.any')}
                  value={get(values, `conditions.${key}`)}
                  onChange={v => setValue(`conditions.${key}`, v)}
                  options={listOptions[key]}
                />
              </Field>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <button type="button" className={btn('secondary')} onClick={onClose} disabled={saving}>
            {t('refundEngine.common.cancel')}
          </button>
          <button type="button" className={btn('primary')} onClick={handleSubmit} disabled={saving}>
            {saving ? t('common.saving') : t('refundEngine.common.save')}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default RefundRuleModal
